import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAttendanceRosterPortal, markAttendancePortal, bulkMarkAttendancePortal,
  getAttendanceHistoryPortal, getHostelAdmissionsPortal, requestAttendanceEditPortal,
  getHostelAdmissionOptionsPortal, getMealsHistoryPortal, getHostelPaymentsPortal, getInvoicesPortal, getInvoicePortal,
  getBillingAdjustmentsPortal, getHostelRequestsPortal,
} from '../../api';
import { FiCheckSquare, FiCheck, FiX, FiUsers, FiClock, FiEdit2, FiLock, FiDownload, FiUser } from 'react-icons/fi';
import { GROUP_OPTIONS, groupRows } from '../../utils/grouping';
import ResidentProfileModal from '../ResidentProfileModal';

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

function downloadCsv(filename, rows) {
  const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function HostelAttendancePortal({ hostel }) {
  const [searchParams] = useSearchParams();
  const urlAdmissionId = searchParams.get('admission_id');

  const [date, setDate] = useState(todayStr());
  const [roster, setRoster] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [editHidden, setEditHidden] = useState(false);
  const [editApproval, setEditApproval] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [admissions, setAdmissions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyStart, setHistoryStart] = useState(firstOfMonth());
  const [historyEnd, setHistoryEnd] = useState(todayStr());
  const [historyAdmissionId, setHistoryAdmissionId] = useState(urlAdmissionId || '');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [groupBy, setGroupBy] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [profileRecord, setProfileRecord] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [reqAdmissionId, setReqAdmissionId] = useState('');
  const [reqStart, setReqStart] = useState(todayStr());
  const [reqEnd, setReqEnd] = useState(todayStr());
  const [reqStatus, setReqStatus] = useState('present');
  const [reqReason, setReqReason] = useState('');
  const [reqSubmitting, setReqSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAttendanceRosterPortal({ hostel_id: hostel.id, date });
      setRoster(res.data.roster);
      setEnabled(res.data.attendance_enabled);
      setEditHidden(res.data.edit_hidden);
      setEditApproval(res.data.edit_approval);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load roster');
    }
    setLoading(false);
  }, [hostel.id, date]);

  useEffect(() => { load(); }, [load]);

  const loadPending = useCallback(async () => {
    try {
      const res = await getHostelRequestsPortal({ status: 'pending' });
      setPendingRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) { /* non-fatal */ }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending]);

  // admission_ids that already have a pending attendance-edit request
  // covering the currently-selected date - their roster row shows a
  // "Pending approval" badge instead of clickable buttons, so re-clicking
  // can't stack up duplicate requests.
  const pendingForDate = useMemo(() => {
    const set = new Set();
    for (const r of pendingRequests) {
      if (r.request_type !== 'attendance_edit') continue;
      const d = typeof r.requested_data === 'string' ? JSON.parse(r.requested_data) : (r.requested_data || {});
      if (d.date_start <= date && date <= d.date_end) set.add(r.admission_id);
    }
    return set;
  }, [pendingRequests, date]);

  useEffect(() => {
    getHostelAdmissionsPortal({ status: 'active' }).then(res => {
      setAdmissions(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
    getHostelAdmissionOptionsPortal().then(res => {
      setCampuses(res.data.campuses || []);
      setCourses(res.data.courses || []);
    }).catch(() => {});
  }, []);

  // Picking a specific resident defaults the history range back to their own
  // enrollment date, so their complete record is visible instead of just
  // "this month".
  useEffect(() => {
    if (!historyAdmissionId) return;
    const adm = admissions.find(a => String(a.id) === String(historyAdmissionId));
    if (adm?.date_of_admission) setHistoryStart(adm.date_of_admission.slice(0, 10));
  }, [historyAdmissionId, admissions]);

  const admissionsById = useMemo(() => Object.fromEntries(admissions.map(a => [a.id, a])), [admissions]);
  const campusNameById = useMemo(() => Object.fromEntries(campuses.map(c => [c.id, c.name])), [campuses]);
  const courseNameById = useMemo(() => Object.fromEntries(courses.map(c => [c.id, c.name])), [courses]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { hostel_id: hostel.id, start: historyStart, end: historyEnd };
      if (historyAdmissionId) params.admission_id = historyAdmissionId;
      const res = await getAttendanceHistoryPortal(params);
      setHistory(res.data);
    } catch (e) { /* non-fatal */ }
    setHistoryLoading(false);
  }, [hostel.id, historyStart, historyEnd, historyAdmissionId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const setStatus = async (admissionId, status) => {
    const row = roster.find(r => r.admission_id === admissionId);
    // Only an actual change to an already-recorded day counts as "editing" -
    // marking a fresh/unmarked day never needs confirmation.
    const isEdit = row && row.attendance_status && row.attendance_status !== status;
    if (isEdit) {
      const msg = editApproval
        ? `${row.full_name} is currently marked ${row.attendance_status} for ${date}. Changing this to ${status} will need admin approval before it takes effect. Continue?`
        : `${row.full_name} is currently marked ${row.attendance_status} for ${date}. Change this to ${status}?`;
      if (!window.confirm(msg)) return;
    }
    setRoster(prev => prev.map(r => r.admission_id === admissionId ? { ...r, attendance_status: status } : r));
    setError(''); setSuccess('');
    try {
      const res = await markAttendancePortal({ admission_id: admissionId, attendance_date: date, status });
      if (res.data.pending) {
        setSuccess(res.data.message || 'Change submitted for admin approval.');
        load();
        loadPending();
      } else {
        loadHistory();
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save');
      load();
    }
  };

  const markAllPresent = async () => {
    if (!window.confirm(`Mark ALL ${roster.length} resident(s) present for ${date}? This will overwrite any existing marks for today.`)) return;
    setSaving(true);
    try {
      const entries = roster.map(r => ({ admission_id: r.admission_id, status: 'present' }));
      await bulkMarkAttendancePortal({ hostel_id: hostel.id, attendance_date: date, entries });
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to bulk mark');
    }
    setSaving(false);
  };

  const openRequestForm = (row) => {
    setReqAdmissionId(row ? row.admission_id : (admissions[0]?.id || ''));
    setReqStart(row ? row.attendance_date.slice(0, 10) : todayStr());
    setReqEnd(row ? row.attendance_date.slice(0, 10) : todayStr());
    setReqStatus(row ? (row.status === 'present' ? 'absent' : 'present') : 'present');
    setReqReason('');
    setShowRequestForm(true);
  };

  const submitRequest = async () => {
    if (!reqAdmissionId || !reqStart || !reqEnd) { setError('Please fill all fields'); return; }
    setReqSubmitting(true); setError(''); setSuccess('');
    try {
      const res = await requestAttendanceEditPortal({
        admission_id: reqAdmissionId, date_start: reqStart, date_end: reqEnd, status: reqStatus, reason: reqReason,
      });
      setSuccess(res.data.message);
      setShowRequestForm(false);
      load(); loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to submit');
    }
    setReqSubmitting(false);
  };

  const viewProfile = (admissionId, fallbackName) => {
    setProfileRecord(admissionsById[admissionId] || { id: admissionId, full_name: fallbackName });
  };

  const exportHistoryCsv = () => {
    const rows = [['Date', 'Resident', 'Status', 'Marked By']];
    history.forEach(h => rows.push([new Date(h.attendance_date).toLocaleDateString('en-PK'), h.full_name, h.status, h.marked_by_name || h.marked_by || '']));
    downloadCsv(`attendance-${hostel.name}-${historyStart}_to_${historyEnd}.csv`, rows);
  };

  const presentCount = roster.filter(r => r.attendance_status === 'present').length;
  const absentCount = roster.filter(r => r.attendance_status === 'absent').length;
  const rosterGroups = groupRows(roster, groupBy, admissionsById, campusNameById, courseNameById);
  const historyGroups = groupRows(history, groupBy, admissionsById, campusNameById, courseNameById);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiCheckSquare /> Daily Attendance</h2>
          <p style={{ margin: '4px 0 0' }}>Mark today's presence for your residents</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <input type="date" className="form-control" value={date} max={todayStr()} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <select className="form-control" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
              {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!enabled && !loading && (
        <div className="alert alert-warning">Attendance tracking has not been enabled for your hostel yet. Contact the admin office to enable it.</div>
      )}

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : enabled && (
        <>
          <div className="stats-grid" style={{ marginBottom: '18px' }}>
            <div className="stat-card stat-purple">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-content"><div className="stat-value">{roster.length}</div><div className="stat-label">Total Residents</div></div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon"><FiCheck /></div>
              <div className="stat-content"><div className="stat-value">{presentCount}</div><div className="stat-label">Present Today</div></div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon"><FiX /></div>
              <div className="stat-content"><div className="stat-value">{absentCount}</div><div className="stat-label">Absent Today</div></div>
            </div>
          </div>

          <div className="action-buttons" style={{ marginBottom: '14px' }}>
            <button className="btn btn-dark btn-small" onClick={markAllPresent} disabled={saving || !roster.length}>
              {saving ? 'Saving...' : 'Mark All Present'}
            </button>
            {!editHidden && (
              <button className="btn btn-secondary btn-small" onClick={() => openRequestForm(null)}>
                <FiEdit2 /> {editApproval ? 'Request a Correction' : 'Correct a Past Date'}
              </button>
            )}
          </div>

          {rosterGroups.map((group, gi) => (
            <div key={gi} style={{ marginBottom: '18px' }}>
              {group.label && <h4 style={{ fontSize: '13px', opacity: 0.7, margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
              <div className="table-container">
                <table className="data-table">
                  <thead><tr><th>#</th><th>Resident</th><th>CNIC</th><th>Status</th><th>Mark</th></tr></thead>
                  <tbody>
                    {group.rows.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>No active residents.</td></tr>
                    ) : group.rows.map((r, i) => (
                      <tr key={r.admission_id}>
                        <td>{i + 1}</td>
                        <td>
                          <strong>{r.full_name}</strong>{' '}
                          <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(r.admission_id, r.full_name)}><FiUser size={12} /></button>
                        </td>
                        <td>{r.cnic || '—'}</td>
                        <td>
                          {r.attendance_status === 'present' && <span className="badge badge-success">Present</span>}
                          {r.attendance_status === 'absent' && <span className="badge badge-danger">Absent</span>}
                          {!r.attendance_status && <span className="badge badge-muted">Not marked</span>}
                        </td>
                        <td>
                          {pendingForDate.has(r.admission_id) ? (
                            <span className="badge badge-gold" title="A change for this date is awaiting admin approval">
                              <FiClock size={12} /> Pending approval
                            </span>
                          ) : (
                            <div className="action-buttons">
                              <button className={`btn btn-small ${r.attendance_status === 'present' ? 'btn-success' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'present')}>Present</button>
                              <button className={`btn btn-small ${r.attendance_status === 'absent' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setStatus(r.admission_id, 'absent')}>Absent</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {/* Request Correction form */}
      {showRequestForm && (
        <div className="modal-overlay" onClick={() => setShowRequestForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3>{editApproval ? 'Request a Correction' : 'Correct a Past Date'}</h3>
              <button className="modal-close" onClick={() => setShowRequestForm(false)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '12px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Resident</label>
                <select className="form-control" value={reqAdmissionId} onChange={e => setReqAdmissionId(e.target.value)}>
                  {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
                </select>
              </div>
              <div className="form-row" style={{ gap: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>From</label>
                  <input type="date" className="form-control" value={reqStart} max={todayStr()} onChange={e => setReqStart(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>To</label>
                  <input type="date" className="form-control" value={reqEnd} max={todayStr()} onChange={e => setReqEnd(e.target.value)} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Correct status to</label>
                <select className="form-control" value={reqStatus} onChange={e => setReqStatus(e.target.value)}>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Reason</label>
                <textarea className="form-control" rows="2" value={reqReason} onChange={e => setReqReason(e.target.value)} placeholder="Why is this correction needed?" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowRequestForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitRequest} disabled={reqSubmitting}>{reqSubmitting ? 'Submitting...' : 'Submit'}</button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div style={{ marginTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FiClock /> Attendance History</h3>
          <button className="btn btn-secondary btn-small" onClick={exportHistoryCsv} disabled={!history.length}><FiDownload /> Export CSV</button>
        </div>
        <div className="form-row" style={{ marginBottom: '14px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Resident</label>
            <select className="form-control" value={historyAdmissionId} onChange={e => setHistoryAdmissionId(e.target.value)}>
              <option value="">All residents</option>
              {admissions.map(a => <option key={a.id} value={a.id}>{a.full_name}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>From{historyAdmissionId ? ' (defaults to enrollment date)' : ''}</label>
            <input type="date" className="form-control" value={historyStart} onChange={e => setHistoryStart(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" className="form-control" value={historyEnd} max={todayStr()} onChange={e => setHistoryEnd(e.target.value)} />
          </div>
        </div>

        {historyLoading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : historyGroups.map((group, gi) => (
          <div key={gi} style={{ marginBottom: '18px' }}>
            {group.label && <h4 style={{ fontSize: '13px', opacity: 0.7, margin: '0 0 8px' }}>{group.label} ({group.rows.length})</h4>}
            <div className="table-container">
              <table className="data-table">
                <thead><tr><th>Date</th><th>Resident</th><th>Status</th><th>Marked By</th>{!editHidden && <th>Action</th>}</tr></thead>
                <tbody>
                  {group.rows.length === 0 ? (
                    <tr><td colSpan={editHidden ? 4 : 5} style={{ textAlign: 'center', padding: '24px' }}>No records in this range.</td></tr>
                  ) : group.rows.map(row => (
                    <tr key={row.id}>
                      <td>{new Date(row.attendance_date).toLocaleDateString('en-PK')}</td>
                      <td>
                        {row.full_name}{' '}
                        <button className="btn btn-small btn-secondary" title="View resident profile" onClick={() => viewProfile(row.admission_id, row.full_name)}><FiUser size={12} /></button>
                      </td>
                      <td>{row.status === 'present' ? <span className="badge badge-success">Present</span> : <span className="badge badge-danger">Absent</span>}</td>
                      <td>{row.marked_by_name || row.marked_by || '—'}</td>
                      {!editHidden && (
                        <td>
                          <button className="btn btn-secondary btn-small" onClick={() => openRequestForm(row)}>
                            <FiEdit2 /> {editApproval ? 'Request Edit' : 'Edit'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
        {editHidden && (
          <p style={{ fontSize: '12px', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FiLock size={12} /> Editing past attendance is disabled for your hostel by the admin office.
          </p>
        )}
      </div>

      {profileRecord && (
        <ResidentProfileModal
          admission={profileRecord}
          campuses={campuses}
          courses={courses}
          onClose={() => setProfileRecord(null)}
          fetchAttendance={(id) => getAttendanceHistoryPortal({ admission_id: id })}
          fetchMeals={(id) => getMealsHistoryPortal({ admission_id: id })}
          fetchPayments={(id) => getHostelPaymentsPortal({ admission_id: id })}
          fetchInvoices={(id) => getInvoicesPortal({ admission_id: id })}
          fetchInvoiceDetail={(id) => getInvoicePortal(id)}
          fetchAdjustments={(id) => getBillingAdjustmentsPortal({ admission_id: id })}
        />
      )}
    </div>
  );
}
